import { EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ElementRef, OnChanges, SimpleChanges, Directive } from '@angular/core';
import { ControlValueAccessor, FormControl, Validator } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Observable, Subject, of } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap, takeUntil, debounceTime } from 'rxjs/operators';

/**
 * Base class for all autocomplete controls
 * Reduces code duplication and provides consistent behavior
 */
@Directive()
export abstract class BaseAutocompleteControl<T> implements OnInit, OnChanges, OnDestroy, ControlValueAccessor, Validator {

    // Common inputs with default values
    @Input() label: string = '';
    @Input() required: boolean = false;
    @Input() submitted: boolean = false;
    @Input() isOutputText: boolean = false;
    @Input() disable: boolean = false;
    @Input() readonly: boolean = false;
    @Input() placeholder: string = '---Lựa chọn---';
    @Input() reload: any = '';
    @Input() appearance: string = 'outline';

    // New: Icon configuration
    @Input() showDropdownIcon: boolean = true;
    @Input() showClearIcon: boolean = true;
    @Input() showLoadingIcon: boolean = true;

    // Output event
    @Output() change = new EventEmitter<T>();
    @Output() selectionChange = new EventEmitter<T>();

    // Form control
    control = new FormControl('');

    // Dropdown trigger - definite assignment assertion
    @ViewChild(MatAutocompleteTrigger) autoComplete!: MatAutocompleteTrigger;
    @ViewChild('input') input!: ElementRef;
    @ViewChild('inputWrapper', { read: ElementRef }) inputWrapper!: ElementRef;

    // Panel width - will be calculated after view init
    panelWidth: string | number = 'auto';

    // Observables - will be initialized in ngOnInit
    filtered$!: Observable<T[]>;
    isLoading = false;

    // Destroy subject for cleanup
    protected destroy$ = new Subject<void>();

    // Cache for loaded item
    protected selectedItem: T | null = null;

    constructor() { }

    ngOnInit(): void {
        this.initializeFiltering();
        this.setupValueChanges();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngAfterViewInit(): void {
        // Calculate panel width based on input-wrapper (includes icons)
        setTimeout(() => {
            if (this.inputWrapper && this.inputWrapper.nativeElement) {
                const wrapperWidth = this.inputWrapper.nativeElement.offsetWidth;
                this.panelWidth = wrapperWidth;
            }
        });
    }

    /**
     * Initialize the filtering logic for autocomplete
     */
    protected initializeFiltering(): void {
        // Số ký tự tối thiểu để gọi API (override nếu cần)
        const minLength = this.getMinSearchLength();

        this.filtered$ = this.control.valueChanges.pipe(
            startWith<string | T>(''),
            // Debounce: chờ user gõ xong mới gọi API
            debounceTime(this.useClientSideFilter() ? 0 : 400),
            // Bỏ qua nếu value là object (đã chọn item → không cần query lại)
            filter(value => typeof value !== 'object' || value === null),
            // Chuyển sang string
            map(value => (typeof value === 'string' ? value : '') as string),
            // Không gọi lại nếu chuỗi search không đổi
            distinctUntilChanged(),
            switchMap(value => {
                // Nếu chuỗi quá ngắn (và không phải client-side), trả về rỗng luôn
                if (!this.useClientSideFilter() && value.length > 0 && value.length < minLength) {
                    return of([] as T[]);
                }

                this.isLoading = !this.useClientSideFilter() && this.showLoadingIcon;
                const result = this.fetchItems(value);

                // Support both Observable and direct array return
                if (Array.isArray(result)) {
                    return of(result);
                }
                return result as Observable<T[]>;
            }),
            map((items: T[]) => {
                this.isLoading = false;
                return items || [];
            }),
            takeUntil(this.destroy$)
        );
    }

    /**
     * Số ký tự tối thiểu để bắt đầu gọi API.
     * Override ở child class nếu muốn thay đổi (default = 2).
     */
    protected getMinSearchLength(): number {
        return 2;
    }

    private lastEmittedValue: any = undefined;

    /**
     * Setup value change subscription
     */
    protected setupValueChanges(): void {
        this.control.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((value) => {
                const isValid = this.isValidItem(value);
                if (isValid) {
                    this.selectedItem = value as T;
                } else {
                    this.selectedItem = null;
                }
                this.outputValue();
            });
    }

    /**
     * Output value to parent form
     */
    protected outputValue(): void {
        let value = this.control.value;

        // If not in text mode and value is invalid, set to null
        if (!this.isOutputText && !this.isValidItem(value)) {
            value = null;
        }

        // Only emit if the conceptual value has changed to avoid feedback loops
        if (value !== this.lastEmittedValue) {
            this.lastEmittedValue = value;
            this.onChange(value);
            this.onTouched();
            this.change.emit(value);
            this.selectionChange.emit(value);
        }
    }

    /**
     * Focus the input field
     */
    focus(): void {
        if (this.input && this.input.nativeElement) {
            this.input.nativeElement.focus();
        }
    }

    /**
     * Handle focusout event
     */
    onFocusOut(): void {
        // Delay validation to allow option selection to complete
        setTimeout(() => {
            if (!this.isOutputText) {
                const value = this.control.value;
                if (!this.isValidItem(value)) {
                    if (this.input && this.input.nativeElement) {
                        this.input.nativeElement.value = '';
                    }
                    this.control.setValue(null);
                }
            }

            // Close panel
            if (this.autoComplete) {
                this.autoComplete.closePanel();
            }
        }, 300); // Increased delay to allow selection
    }

    /**
     * Manually trigger dropdown
     */
    openDropdown(): void {
        if (this.autoComplete && !this.disable && !this.readonly) {
            this.autoComplete.openPanel();
        }
    }

    /**
     * Clear the control value
     */
    clear(): void {
        this.control.reset('', { emitEvent: true });
        this.selectedItem = null;
        this.outputValue();
    }

    // ============================================
    // ControlValueAccessor Implementation
    // ============================================

    writeValue(obj: any): void {
        // Update lastEmittedValue to current incoming value to prevent echoing it back
        this.lastEmittedValue = obj;

        if (this.isValidItem(obj)) {
            this.control.setValue(obj, { emitEvent: false });
            this.selectedItem = obj as T;
        } else if (obj === null || obj === undefined || obj === '') {
            // Check if we are currently typing to avoid clearing the search text during focus
            const currentValue = this.control.value;
            const isTyping = typeof currentValue === 'string' && currentValue !== '' && !this.isValidItem(currentValue);
            const isFocused = this.input && this.input.nativeElement === document.activeElement;

            if (isTyping && !this.isOutputText && isFocused) {
                // User is actively typing search string, and parent sent null/empty (feedback loop).
                // We keep the typed text locally to avoid clearing the input on every keystroke.
                this.selectedItem = null;
                return;
            }

            this.control.reset('', { emitEvent: false });
            this.selectedItem = null;
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        isDisabled ? this.control.disable() : this.control.enable();
    }

    // Validator implementation
    validate(c: FormControl): any {
        return this.control.errors;
    }

    // Placeholder functions (will be overridden by Angular)
    onChange: any = (value: any) => { };
    onTouched: any = () => { };

    // ============================================
    // Abstract Methods (must be implemented by child classes)
    // ============================================

    /**
     * Fetch items from API based on search value
     * Can return Observable<T[]> or T[] directly for client-side filtering
     */
    protected abstract fetchItems(searchValue: string): Observable<T[]> | T[];

    /**
     * Get display value for an item
     */
    abstract getDisplayValue(item: T | null): string;

    /**
     * Check if value is a valid item instance
     */
    protected abstract isValidItem(value: any): boolean;

    /**
     * Override this if using client-side filtering (returns false by default)
     */
    protected useClientSideFilter(): boolean {
        return false;
    }

    /**
     * Handle reload changes (optional override)
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes.reload) {
            // Re-initialize filtering when reload/trigger changes
            this.initializeFiltering();
        }
    }
}
